import React, {useState, useEffect} from 'react';
import {
    Book,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Search,
    CheckCircle,
    XCircle,
    Filter,
    Clock,
    User,
    Bot
} from 'lucide-react';

const RequestedBooksPage = () => {
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [statusUpdates, setStatusUpdates] = useState({});

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/admin/book-requests/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch book requests: ${response.statusText}`);
            }

            const data = await response.json();
            // console.log("requested book data", data);
            setRequests(data);

            // Initialize status updates
            const updates = {};
            data.forEach(request => {
                updates[request.request_id] = request.status;
            });
            setStatusUpdates(updates);
        } catch (error) {
            console.error('Error fetching book requests:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = (event, requestId) => {
        setStatusUpdates({
            ...statusUpdates,
            [requestId]: event.target.value,
        });
    };

    const updateRequestStatus = async (requestId) => {
        const selectedStatus = statusUpdates[requestId];
        if (!selectedStatus) {
            alert('Please select a status to update.');
            return;
        }

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/admin/update-book-request/', {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: selectedStatus,
                    request_id: requestId,
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to update request status: ${response.statusText}`);
            }

            // Update the request in the state
            setRequests(prevRequests =>
                prevRequests.map(request =>
                    request.request_id === requestId
                        ? {...request, status: selectedStatus}
                        : request
                )
            );

            alert("Request status updated successfully");
        } catch (error) {
            console.error('Error updating request status:', error);
            alert(`Failed to update request status: ${error.message}`);
        }
    };

    // Filter requests by search term and status
    const filteredRequests = requests.filter(request => {
        const matchesSearch =
            request.book_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.book_author.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.book_category.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.bot_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (request.applicant_email && request.applicant_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (request.applicant_sect && request.applicant_sect.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus = filterStatus ? request.status === filterStatus : true;

        return matchesSearch && matchesStatus;
    });

    // Function to get appropriate status badge styling
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'fulfilled':
                return 'bg-green-100 text-green-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-yellow-100 text-yellow-800';
        }
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Book className="text-blue-600" size={32}/>
                    Requested Books
                </h1>
                <p className="mt-2 text-gray-600">
                    Manage and fulfill book requests from Maktab chatbot users
                </p>
            </div>

            {/* Filters and search */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="relative w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400"/>
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search books..."
                            className="pl-10 w-full border border-gray-300 rounded-md py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <div className="inline-flex items-center">
                            <Filter size={14} className="text-gray-500 mr-1"/>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="fulfilled">Fulfilled</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>

                        <button
                            onClick={fetchRequests}
                            className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1"/>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Book Requests */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 size={40} className="animate-spin text-blue-600"/>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading book requests</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    {filteredRequests.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Book Details
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User Info
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Chatbot
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Requested
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {filteredRequests.map((request) => (
                                    <tr key={request.request_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="flex items-start">
                                                <div
                                                    className="flex-shrink-0 h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                                                    <Book className="h-5 w-5 text-blue-600"/>
                                                </div>
                                                <div className="ml-4">
                                                    <div
                                                        className="text-sm font-medium text-gray-900">{request.book_name}</div>
                                                    <div className="text-xs text-gray-500">
                                                        <span
                                                            className="text-gray-600 font-medium">Author:</span> {request.book_author || 'Not specified'}
                                                    </div>
                                                    {request.book_category && (
                                                        <div className="text-xs text-gray-500">
                                                            <span
                                                                className="text-gray-600 font-medium">Category:</span> {request.book_category}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div
                                                    className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                                    <User className="h-4 w-4 text-indigo-600"/>
                                                </div>
                                                <div className="ml-3">
                                                    <div
                                                        className="text-sm font-medium text-gray-900">{request.applicant_email || 'N/A'}</div>
                                                    {request.applicant_sect && (
                                                        <div className="text-xs text-gray-500">
                                                            <span className="text-gray-600 font-medium">Sect:</span> {request.applicant_sect}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div
                                                    className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                    <Bot className="h-4 w-4 text-purple-600"/>
                                                </div>
                                                <div className="ml-3">
                                                    <div
                                                        className="text-sm font-medium text-gray-900">{request.bot_name}</div>
                                                    <div className="text-xs text-gray-500">ID: {request.bot_id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center">
                                                <Clock size={14} className="mr-1 text-gray-400"/>
                                                {new Date(request.requested_at).toLocaleString()}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                        <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(request.status)}`}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex items-center space-x-2">
                                                <select
                                                    value={statusUpdates[request.request_id] || request.status}
                                                    onChange={(e) => handleStatusChange(e, request.request_id)}
                                                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="fulfilled">Fulfilled</option>
                                                    <option value="rejected">Rejected</option>
                                                </select>
                                                <button
                                                    onClick={() => updateRequestStatus(request.request_id)}
                                                    disabled={statusUpdates[request.request_id] === request.status}
                                                    className={`px-3 py-1 rounded text-xs font-medium ${
                                                        statusUpdates[request.request_id] === request.status
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors'
                                                    }`}
                                                >
                                                    Update
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            {searchTerm || filterStatus ? (
                                <div className="flex flex-col items-center">
                                    <Search size={40} className="text-gray-400 mb-4"/>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">No book requests found</h3>
                                    <p className="text-gray-600 mb-4">
                                        No book requests match your current search filters
                                    </p>
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setFilterStatus('');
                                        }}
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                        Clear all filters
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center">
                                    <Book size={40} className="text-gray-400 mb-4"/>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">No book requests
                                        available</h3>
                                    <p className="text-gray-600">
                                        There are no book requests to display
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Info Card */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <Book size={20} className="text-blue-600"/>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">About Book Requests</h3>
                        <div className="mt-2 text-sm text-blue-700">
                            <p>
                                Maktab chatbot users can request books that they'd like to have included in their
                                chatbot's knowledge base.
                                Review these requests and mark them as fulfilled when you've added the requested book to
                                the system.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RequestedBooksPage;